import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';

import { isMobilePage } from 'lib/util/helpers';

import { Product } from './Product';
import { Availability, AvailabilityStatus } from './Availability';
import {
	simpleSubscribe,
	ListenerModule,
	ExposedProductInfo,
} from 'lib/ecommerce/Tc.Module.EcommerceProductDataHub';

const CONTAINER_CLASS = 'availabilityInfo';

/**
 * Online Availability Info
 *
 * @author Matthias Inauen
 * @namespace Tc.Module
 * @class Online Availability Info
 * @extends Tc.Module
 */
export default class OnlineAvailabilityInfo extends Module implements ListenerModule {
	// Data about the current product for getting the following attributes:
	// • delayedDeliveryReason: a text to display if delivery is delayed
	// • reservationAllowed: a boolean determining if the product can be reserved if out-of-stock
	// Can be null, in which case reservation is not possible
	private productData? : Product;
	// Data about the product’s availability. Always an instance of Module.EcommerceProductDataHub.Availability
	private productAvailabilityInfo! : Availability;

	private productId? : string;
	private container! : JQuery;
	private containerStyle! : string;
	private subcontainer! : JQuery;
	private status! : string;

	public on(callback : Hook) {
		this.container = $('.' + CONTAINER_CLASS, this.$ctx);
		const classes = this.container.attr('class');
		this.containerStyle = classes || '';
		this.subcontainer = $('.availabilityInfoDelayedDeliveryInfoLink', this.$ctx);
		this.productId = this.$ctx.data('productid');
		this.status = this.$ctx.data('status');

		if (this.productId) {
			this.productAvailabilityInfo = new Availability(this.productId);
		} else {
			// tslint:disable-next-line:no-unbound-method
			simpleSubscribe(this, this.gotDataFromHub);
		}

		callback();
	}

	public after() {
		if (this.productId) {
			this.update();
		}
	}

	private gotDataFromHub(info : ExposedProductInfo) {
		if(info && info.product) {
			this.productData = info.product;
			this.productAvailabilityInfo = info.product.getAvailability();
			this.update();
		}
	}

	private update() {
		if(!this.productAvailabilityInfo) {
			this.log.error('No product data in OnlineAvailabilityInfo module');

			return;
		}

		if(!this.productAvailabilityInfo.loaded()) {
			// Availability info not yet available, wait until loaded.
			// tslint:disable-next-line:no-floating-promises
			this.productAvailabilityInfo.load().done(this.update.bind(this));

			return;
		}

		const data = this.getUpdateData(this.productData!, this.productAvailabilityInfo);

		// Desktop has mod.status='always'
		if (
			data.infoText &&
			(
				!this.status ||
				this.status === 'always' ||
				this.status.toLowerCase() === data.availability.toLowerCase()
			)
		) {
			if (this.$ctx.hasClass('Classic')) {
				this.container
					.removeClass()
					.addClass(CONTAINER_CLASS)
					.addClass('stock-availability-' + data.availability)
					.text(data.infoText)
					.css('display', 'block');

				return;
			}

			let iconClass = '';
			const iconPrefix = 'icon ';
			let colorClass = '';
			switch(data.availability) {
				case 'OnStock':
					iconClass = iconPrefix + 'icon-012-check-mark-circle';
					colorClass = 'int-green';
					break;
				case 'ShortnessOfSupplies':
					iconClass = iconPrefix + 'icon-026-exclamation-mark-circle';
					colorClass = 'int-orange';
					break;
				case 'OutOfStockReservable':
					iconClass = iconPrefix + 'icon-021-clock';
					break;
				case 'OutOfStockNotifiable':
				case 'OutOfStock':
					iconClass = iconPrefix + 'icon-026-exclamation-mark-circle';
					colorClass = 'int-red';
					break;
				default:
					break;
			}

			if(data.availability) {
				this.container
					.removeClass()
					.addClass(this.containerStyle)
					.removeClass('hidden')
					.addClass(colorClass)
					.children('.infoText')
					.text(data.infoText);
				this.container.children('i').removeClass().addClass(iconClass);
			}

			if (this.subcontainer.length) {
				this.subcontainer.toggleClass('hidden', data.availability !== AvailabilityStatus.DelayedDelivery);
				if (isMobilePage()) {
					this.container.eq(0).toggleClass('hidden', data.availability === AvailabilityStatus.DelayedDelivery);
				}
			}
		} else {
			// Clear availability info
			this.subcontainer.addClass('hidden');
			this.container.eq(0).addClass('hidden');
		}
	}

	private getUpdateData(productData : Product, productAvailabilityInfo : Availability) {
		const updateData = {
			availability: productAvailabilityInfo.getStatus(),
			infoText: this.$ctx.data(productAvailabilityInfo.getStatus().toLowerCase()),
		};

		if(productData) {
			// Product info came from data hub
			const isReservationAllowed = productData.isReservationAllowed();
			const delayedDeliveryReason = productData.getDelayedDeliveryReason();

			if (isReservationAllowed && delayedDeliveryReason) {
				updateData.availability = AvailabilityStatus.DelayedDelivery;
				updateData.infoText = delayedDeliveryReason;
			}

			this.$ctx.toggleClass('two-columns', productData.isReservationInStoreAllowed());
		}

		return updateData;
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Tc.Module.OnlineAvailabilityInfo.ts